import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {Trans, useTranslation} from "react-i18next";
import toast from "react-hot-toast";
import {Link} from "react-router-dom";

import type {AppDispatch, RootState} from "../../../store";
import {useRestaurantDetails} from "../../providers/RestaurantDetailsContext";
import {useOrderFlow} from "../../providers/OrderFlowProvider";
import useOrderNotifications from "../../../hooks/useOrderNotifications";
import useBrowserOrderNotification from "../../../hooks/browserOrderNotification";
import {getCheckoutPrintErrorMessage, printCheckoutReceipt} from "../../../helpers/checkoutReceiptPrinting";
import {addToOutOfPendingStatusCheckouts} from "../../../store/slices/paymentManagementSlice";
import {changeCheckoutDetailsOfCurrentOrder} from "../../../store/slices/stepManagementSlice";
import {isWaiterCreatedOrder} from "../../../helpers/uiConditions";
import {sendAxiosRequestWithRetry} from "../../../services/sendAxiosRequestWithRetry";
import useStableWebSocket from "../../../hooks/useStableWebsocket";
import { OrderSocketMessage, TransactionSocketMessage } from "../../../shared/types/socket";

const ORDER_MODAL_DEDUP_WINDOW_MS = 60 * 1000;

type UseAppLayoutRealtimeResult = {
    closeOrderNotificationModal: (orderKeyToClose: string) => void;
    getOrderCreatedAtMs: (orderDetails?: CashBoxOrderItemData | null) => number | null;
    getOrderTrackingKey: (orderDetails?: CashBoxOrderItemData | null) => string | null;
    openOrderNotificationModals: CashBoxOrderItemData[];
};

const useAppLayoutRealtime = (): UseAppLayoutRealtimeResult => {
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();

    const {
        updateOrdersList,
        refetchOrderDetailsAndUpdate,
    } = useOrderFlow();

    const { restaurantDetails } = useRestaurantDetails();
    const { ws_url } = restaurantDetails || {};

    const pendingPaymentCheckouts = useSelector((state: RootState) => state.paymentManagement.pendingPaymentCheckouts);
    const currentTableId = useSelector((state: RootState) => state.stepManagement.currentTableId);
    const currentOrderType = useSelector((state: RootState) => state.stepManagement.currentOrderType);
    const currentOrderId = useSelector((state: RootState) => state.stepManagement.currentOrderId);
    const notificationOrders = useSelector((state: RootState) => state.notifications.orders);
    const excludedNotificationOrdersData = useSelector((state: RootState) => state.notifications.excludedOrders);
    const printMethod = useSelector((state: RootState) => state.appGeneral.printMethod);
    const autoPrint = useSelector((state: RootState) => state.appGeneral.autoPrint);
    const printerSize = useSelector((state: RootState) => state.appGeneral.printerSize);
    const shouldAutoPrint = printMethod !== "browser" && autoPrint;

    const filteredNotificationOrders = useMemo(() => {
        return (notificationOrders || []).filter(
            (order) => !Object.keys(excludedNotificationOrdersData || {}).includes(order?.id?.toString())
        );
    }, [notificationOrders, excludedNotificationOrdersData]);

    const { refetchOrderNotifications } = useOrderNotifications();
    const { OrderNotificationBrowserAlert } = useBrowserOrderNotification();

    const [openOrderNotificationModals, setOpenOrderNotificationModals] = useState<CashBoxOrderItemData[]>([]);

    // const hasWsConnectedOnceRef = useRef(false);
    const latestShownOrderCreatedAtMsRef = useRef<number | null>(null);
    const recentlyShownOrderKeysRef = useRef(new Map<string, number>());

    const getOrderTrackingKey = useCallback((orderDetails?: CashBoxOrderItemData | null) => {
        if (!orderDetails) return null;

        if (orderDetails.id !== undefined && orderDetails.id !== null) {
            return `id:${orderDetails.id}`;
        }

        if (orderDetails.created_at_timezone) {
            return `created_at:${orderDetails.created_at_timezone}`;
        }

        return null;
    }, []);

    const getOrderCreatedAtMs = useCallback((orderDetails?: CashBoxOrderItemData | null) => {
        const createdAt = orderDetails?.created_at_timezone;
        if (!createdAt) return null;

        const createdAtMs = Date.parse(createdAt);
        if (Number.isNaN(createdAtMs)) return null;

        return createdAtMs;
    }, []);

    const pruneRecentlyShownOrderKeys = useCallback((referenceCreatedAtMs?: number | null) => {
        if (!Number.isFinite(referenceCreatedAtMs)) return;

        const minAllowedCreatedAtMs = Number(referenceCreatedAtMs) - ORDER_MODAL_DEDUP_WINDOW_MS;

        recentlyShownOrderKeysRef.current.forEach((orderCreatedAtMs, orderKey) => {
            if (
                !Number.isFinite(orderCreatedAtMs) ||
                orderCreatedAtMs < minAllowedCreatedAtMs
            ) {
                recentlyShownOrderKeysRef.current.delete(orderKey);
            }
        });
    }, []);

    const markOrderModalAsShown = useCallback((orderDetails?: CashBoxOrderItemData | null) => {
        const orderKey = getOrderTrackingKey(orderDetails);
        if (!orderKey) return;

        const createdAtMs = getOrderCreatedAtMs(orderDetails);
        if (
            createdAtMs !== null &&
            Number.isFinite(createdAtMs) &&
            (
                latestShownOrderCreatedAtMsRef.current === null ||
                createdAtMs > latestShownOrderCreatedAtMsRef.current
            )
        ) {
            latestShownOrderCreatedAtMsRef.current = createdAtMs;
        }

        const storedCreatedAtMs =
            createdAtMs !== null && Number.isFinite(createdAtMs)
                ? createdAtMs
                : (latestShownOrderCreatedAtMsRef.current ?? Date.now());

        recentlyShownOrderKeysRef.current.set(orderKey, storedCreatedAtMs);

        if (Number.isFinite(latestShownOrderCreatedAtMsRef.current)) {
            pruneRecentlyShownOrderKeys(latestShownOrderCreatedAtMsRef.current);
        }
    }, [getOrderTrackingKey, getOrderCreatedAtMs, pruneRecentlyShownOrderKeys]);

    const addOrderNotificationModal = useCallback((orderDetails?: CashBoxOrderItemData | null) => {
        const orderKey = getOrderTrackingKey(orderDetails);
        if (!orderKey) return;

        pruneRecentlyShownOrderKeys(latestShownOrderCreatedAtMsRef.current);

        if (recentlyShownOrderKeysRef.current.has(orderKey)) return;

        markOrderModalAsShown(orderDetails);

        setOpenOrderNotificationModals((prevModals) => {
            if (!orderDetails) return prevModals;

            const alreadyOpen = prevModals.some(
                (modalOrder) => getOrderTrackingKey(modalOrder) === orderKey
            );
            if (alreadyOpen) return prevModals;

            return [...prevModals, orderDetails].sort((a, b) => {
                const aCreatedAtMs = getOrderCreatedAtMs(a) ?? Number.MAX_SAFE_INTEGER;
                const bCreatedAtMs = getOrderCreatedAtMs(b) ?? Number.MAX_SAFE_INTEGER;
                return aCreatedAtMs - bCreatedAtMs;
            });
        });
    }, [getOrderTrackingKey, getOrderCreatedAtMs, markOrderModalAsShown, pruneRecentlyShownOrderKeys]);

    const closeOrderNotificationModal = useCallback((orderKeyToClose: string) => {
        setOpenOrderNotificationModals((prevModals) =>
            prevModals.filter(
                (modalOrder) => getOrderTrackingKey(modalOrder) !== orderKeyToClose
            )
        );
    }, [getOrderTrackingKey]);

    const handleShowNewReceivedOrderModals = useCallback(async () => {
        const nextNotificationOrders = await refetchOrderNotifications();

        if (Array.isArray(nextNotificationOrders) && nextNotificationOrders.length) {
            const baselineCreatedAtMs = latestShownOrderCreatedAtMsRef.current ?? 0;
            const minCreatedAtMs = baselineCreatedAtMs - ORDER_MODAL_DEDUP_WINDOW_MS;
            const shownKeysBeforeReconnect = new Set(recentlyShownOrderKeysRef.current.keys());

            nextNotificationOrders
                .filter((orderDetails) => {
                    const orderCreatedAtMs = getOrderCreatedAtMs(orderDetails);
                    return (
                        orderCreatedAtMs !== null &&
                        Number.isFinite(orderCreatedAtMs) &&
                        orderCreatedAtMs >= minCreatedAtMs
                    );
                })
                .sort((a, b) => {
                    const aCreatedAtMs = getOrderCreatedAtMs(a) ?? Number.MAX_SAFE_INTEGER;
                    const bCreatedAtMs = getOrderCreatedAtMs(b) ?? Number.MAX_SAFE_INTEGER;
                    return aCreatedAtMs - bCreatedAtMs;
                })
                .forEach((orderDetails) => {
                    const orderKey = getOrderTrackingKey(orderDetails);
                    if (orderKey && shownKeysBeforeReconnect.has(orderKey)) return;
                    addOrderNotificationModal(orderDetails);
                });
        }
    }, [addOrderNotificationModal, getOrderCreatedAtMs, getOrderTrackingKey, refetchOrderNotifications]);

    const showPrintingErrorToast = useCallback((error: unknown) => {
        const errorText = String(getCheckoutPrintErrorMessage({
            error,
            t,
        }));

        toast.error(
            <div>
                <h6 className="text-capitalize">
                    {t("printingFailed")}
                </h6>
                <div>{errorText}</div>
            </div>,
            {
                duration: 8000,
            }
        );
    }, [t]);

    const handlePrintOrderPaymentDetails = useCallback(async (data: TransactionCheckoutUpdatePayload) => {
        if (!data.order_pay) {
            showPrintingErrorToast({ message: t("checkoutPrintDataUnavailable") });
            return;
        }

        // let targetOrderDetails: CashBoxOrderDetails;
        // if (currentOrderDetails?.id === data.order_id) {
        //     targetOrderDetails = currentOrderDetails;
        // }
        // else {
        const {
            data: targetOrderDetails,
            // error: targetOrderDetailsRespError,
        } = await sendAxiosRequestWithRetry<CashBoxOrderDetails>({
            method: "get",
            url: "/order/details",
            params: {
                order_id: data.order_id
            },
        });
        //     targetOrderDetails = respData;
        // }

        if (!targetOrderDetails) {
            return;
        }

        if (printMethod === "browser") {
            // printDataInCurrentWindow("transaction_receipt_data");
            return;
        }

        // if (printingRequestIsPending) {
        //     return;
        // }

        // setPrintingRequestIsPending(true);

        const printResult = await printCheckoutReceipt({
            printMethod,
            checkoutId: data.checkout_id,
            allowBluetoothDeviceSelection: false,
            allowUsbDeviceSelection: false,
            printPayload: {
                printerSize,
                restaurantName: restaurantDetails?.restaurant_name,
                restaurantLogo: restaurantDetails?.restaurant_logo,
                waiterName: restaurantDetails?.waiter_name || restaurantDetails?.waiter_email,
                currency: restaurantDetails?.currency,
                orderId: data.order_id,
                checkoutUpdatedAt: data.order_pay.updated_at,
                ordersPrice: data.order_pay.orders_price,
                discountPrice: data.order_pay.discount_price,
                tipPrice: data.order_pay.tip_price,
                paidPrice: data.order_pay.paid_price,
                taxes: Array.isArray(data.order_pay.taxes) ? data.order_pay.taxes : [],
                qrBase64: data.order_pay.kasse_service?.qr_base64,
                qrSignatureText: data.order_pay.kasse_service?.signature_text,
                items: targetOrderDetails.items,
                customItems: targetOrderDetails.customItems,
                manualItems: targetOrderDetails.freeItems,
            },
        });

        if (!printResult?.success && !printResult?.skipped) {
            showPrintingErrorToast(printResult?.error);
        }

        // setPrintingRequestIsPending(false);
    }, [printMethod, printerSize, restaurantDetails, showPrintingErrorToast, t]);

    const updateOrderOrTablesDataIfNeeded = useCallback((targetOfReceivedMessage?: {
        table_id?: number;
        order_type?: ApiOrderType;
        order_id?: number;
    }) => {
        if (
            !targetOfReceivedMessage ||
            (currentTableId && currentTableId === targetOfReceivedMessage.table_id) ||
            (currentOrderType && currentOrderType === targetOfReceivedMessage.order_type)
        ) {
            updateOrdersList({
                tableId: currentTableId,
                orderType: currentOrderType,
                silentFetching: true,
            });
        }

        if (
            !targetOfReceivedMessage ||
            (currentOrderId && currentOrderId === targetOfReceivedMessage.order_id)
        ) {
            refetchOrderDetailsAndUpdate({
                orderId: currentOrderId,
                silentSelecting: true,
            });
        }
    }, [currentOrderId, currentOrderType, currentTableId, refetchOrderDetailsAndUpdate, updateOrdersList]);

    const onGetSuccessTransactionSocketMessage = useCallback((mess_data: TransactionSocketMessage) => {
        if (mess_data.type !== "success_viva_wallet_transaction") return;

        const {
            // demo_mode,
            data,
        } = mess_data;

        const {
            order_id,
            table_id,
            order_type,
            order_pay,
        } = data || {};

        const {
            checkout_id,
            waiter_id,
        } = order_pay || {};

        if (checkout_id && pendingPaymentCheckouts?.[checkout_id]) {
            dispatch(addToOutOfPendingStatusCheckouts({
                table_id,
                order_type,
                order_id,
                checkout_id,
            }));
        }

        dispatch(changeCheckoutDetailsOfCurrentOrder({
            new_checkoutDetails: data,
        }));

        if (
            // waiter_id !== restaurantDetails?.waiter_id &&
            checkout_id &&
            pendingPaymentCheckouts?.[checkout_id]
        ) {
            toast.success((tst) => (
                <div className="toast-content-with-inline-link">
                    <Trans
                        i18nKey="checkoutHasPaidByPosInJSX"
                        values={{ checkout_id, order_id }}
                        components={[
                            <></>,
                            <strong />,
                            <></>,
                            <strong />,
                            <></>,
                            <Link className="" to={{ pathname: "/", search: `?tableId=${table_id}&orderType=${order_type}&orderId=${order_id}` }} />,
                        ]}
                    />
                    <button className="btn dismiss-btn" onClick={() => toast.dismiss(tst.id)}>
                        &times;
                    </button>
                </div>
            ), {
                duration: 8000,
            });
        }

        if (waiter_id && shouldAutoPrint) {
            handlePrintOrderPaymentDetails(data);
        }

        updateOrderOrTablesDataIfNeeded({
            order_id,
            table_id: table_id || undefined,
            order_type,
        });
    }, [dispatch, handlePrintOrderPaymentDetails, pendingPaymentCheckouts, restaurantDetails?.waiter_id, shouldAutoPrint, updateOrderOrTablesDataIfNeeded]);

    const onGetCreateOrUpdateReceiptOrDeleteOrderSocketMessage = useCallback((mess_data: OrderSocketMessage) => {
        if (
            mess_data.type !== "create_receipt" &&
            mess_data.type !== "update_receipt" &&
            mess_data.type !== "delete_order"
        ) {
            return;
        }

        const socketOrder = mess_data?.data;

        if (mess_data.type === "delete_order") {
            updateOrderOrTablesDataIfNeeded();
            return;
        }

        if (!socketOrder) {
            return;
        }

        const socketOrderKey = getOrderTrackingKey(socketOrder);
        const hasOrderPayload = Boolean(socketOrderKey);
        const isCustomerOrder = !isWaiterCreatedOrder(socketOrder);

        if (hasOrderPayload && isCustomerOrder) {
            OrderNotificationBrowserAlert(socketOrder);

            if (mess_data.type === "create_receipt") {
                addOrderNotificationModal(socketOrder);
            }
        }

        updateOrderOrTablesDataIfNeeded({
            order_id: socketOrder.id,
            table_id: socketOrder.table_id || undefined,
            order_type: socketOrder.order_type || undefined,
        });

        refetchOrderNotifications();
    }, [OrderNotificationBrowserAlert, addOrderNotificationModal, getOrderTrackingKey, refetchOrderNotifications, updateOrderOrTablesDataIfNeeded]);

    useStableWebSocket({
        url: ws_url,
        onOpen: async () => {
            console.log("WS connected");

            // if (!hasWsConnectedOnceRef.current) {
            //     handleShowNewReceivedOrderModals();
            //     hasWsConnectedOnceRef.current = true;
            //     return;
            // }

            handleShowNewReceivedOrderModals();
            updateOrderOrTablesDataIfNeeded();
        },
        onMessage: (mess_data: TransactionSocketMessage | OrderSocketMessage) => {
            if (process.env.NODE_ENV === "development") {
                console.log("WS mess: ", mess_data);
            }

            if (mess_data.type === "success_viva_wallet_transaction") {
                onGetSuccessTransactionSocketMessage(mess_data);
            }

            if (
                mess_data.type === "create_receipt" ||
                mess_data.type === "update_receipt" ||
                mess_data.type === "delete_order"
            ) {
                onGetCreateOrUpdateReceiptOrDeleteOrderSocketMessage(mess_data);
            }
        },
        onError: (error: unknown) => {
            console.error("WS error", error);
        },
        onClose: () => {
            console.log("WS closed");
        },
    });

    useEffect(() => {
        // hasWsConnectedOnceRef.current = false;
        latestShownOrderCreatedAtMsRef.current = null;
        recentlyShownOrderKeysRef.current = new Map();
        setOpenOrderNotificationModals([]);
    }, [ws_url]);

    useEffect(() => {
        const filteredNotificationOrderIds = new Set(
            filteredNotificationOrders.map(order => order?.id)
        );

        setOpenOrderNotificationModals((prevModals) => (
            prevModals.filter((modalOrder) => {
                if (!modalOrder?.id) return true;
                return filteredNotificationOrderIds.has(modalOrder.id);
            })
        ));
    }, [filteredNotificationOrders]);

    return {
        closeOrderNotificationModal,
        getOrderCreatedAtMs,
        getOrderTrackingKey,
        openOrderNotificationModals,
    };
};

export default useAppLayoutRealtime;
